import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
  type CollectionReference,
  type DocumentData,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import type {
  CreateMedicalTemplateInput,
  MedicalTemplate,
  UpdateMedicalTemplateInput,
} from "@/features/medicalTemplates/schemas/medicalTemplate";

const MEDICAL_TEMPLATES_COLLECTION = "medical_templates";

function getMedicalTemplatesCollection(): CollectionReference<DocumentData> {
  return collection(db, MEDICAL_TEMPLATES_COLLECTION);
}

function assertMedicalTemplateFields(input: {
  name: string;
  category: string;
  prompt: string;
}): { name: string; category: string; prompt: string } {
  const name = input.name.trim();
  const category = input.category.trim();
  const prompt = input.prompt.trim();

  if (!name) {
    throw new Error("El nombre de la plantilla es obligatorio");
  }
  if (!category) {
    throw new Error("La categoría es obligatoria");
  }
  if (!prompt) {
    throw new Error("El prompt es obligatorio");
  }

  return { name, category, prompt };
}

/**
 * Lista las plantillas de historia médica desde Firestore.
 * Solo incluye `aiTemplate: true` — las que se gestionan desde la plataforma.
 */
export async function listMedicalTemplates(): Promise<MedicalTemplate[]> {
  const snapshot = await getDocs(getMedicalTemplatesCollection());

  const items: MedicalTemplate[] = snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      docId: docSnap.id,
      name:
        typeof data.name === "string"
          ? data.name
          : typeof data.title === "string"
            ? data.title
            : "",
      description:
        typeof data.description === "string" ? data.description : undefined,
      category: typeof data.category === "string" ? data.category : "",
      formFields:
        typeof data.formFields === "string" ? data.formFields : undefined,
      content: typeof data.content === "string" ? data.content : undefined,
      prompt: typeof data.prompt === "string" ? data.prompt : "",
      aiTemplate: data.aiTemplate === true,
      isActive: data.isActive !== false,
      createdBy:
        typeof data.createdBy === "string"
          ? data.createdBy
          : typeof data.doctor_id === "string"
            ? data.doctor_id
            : undefined,
      createdAt: data.createdAt ?? data.created_at ?? undefined,
      updatedAt: data.updatedAt ?? data.updated_at ?? undefined,
    };
  });

  return items
    .filter((tpl) => tpl.name.length > 0 && tpl.aiTemplate)
    .sort((a, b) => {
      const byCategory = a.category.localeCompare(b.category, "es");
      if (byCategory !== 0) return byCategory;
      return a.name.localeCompare(b.name, "es");
    });
}

/**
 * Crea una plantilla en `medical_templates` (doc id auto).
 * Siempre se crea con `aiTemplate: true` — este editor solo gestiona ese subconjunto.
 * Escribe ambas claves de los pares legado (name/title, createdBy/doctor_id,
 * createdAt/created_at, updatedAt/updated_at) para no romper otros clientes.
 */
export async function createMedicalTemplate(
  input: CreateMedicalTemplateInput,
  authorUid: string
): Promise<void> {
  const fields = assertMedicalTemplateFields(input);
  const now = serverTimestamp();

  const payload = {
    name: fields.name,
    title: fields.name,
    description: input.description?.trim() || null,
    category: fields.category,
    formFields: input.formFields?.trim() || null,
    content: input.content?.trim() || null,
    prompt: fields.prompt,
    aiTemplate: true,
    isActive: input.isActive,
    createdBy: authorUid,
    doctor_id: authorUid,
    createdAt: now,
    created_at: now,
    updatedAt: now,
    updated_at: now,
  };

  await addDoc(getMedicalTemplatesCollection(), payload);
}

/**
 * Actualiza una plantilla existente por document id. No toca autor/fecha de creación.
 */
export async function updateMedicalTemplate(
  docId: string,
  input: UpdateMedicalTemplateInput
): Promise<void> {
  const ref = doc(db, MEDICAL_TEMPLATES_COLLECTION, docId);
  const payload: Record<string, unknown> = {};

  if (input.name !== undefined) {
    const name = input.name.trim();
    if (!name) {
      throw new Error("El nombre de la plantilla es obligatorio");
    }
    payload.name = name;
    payload.title = name;
  }
  if (input.category !== undefined) {
    const category = input.category.trim();
    if (!category) {
      throw new Error("La categoría es obligatoria");
    }
    payload.category = category;
  }
  if (input.prompt !== undefined) {
    const prompt = input.prompt.trim();
    if (!prompt) {
      throw new Error("El prompt es obligatorio");
    }
    payload.prompt = prompt;
  }
  if (input.description !== undefined) {
    payload.description = input.description.trim() || null;
  }
  if (input.formFields !== undefined) {
    payload.formFields = input.formFields.trim() || null;
  }
  if (input.content !== undefined) {
    payload.content = input.content.trim() || null;
  }
  if (input.isActive !== undefined) {
    payload.isActive = input.isActive;
  }

  if (Object.keys(payload).length === 0) return;

  const now = serverTimestamp();
  payload.updatedAt = now;
  payload.updated_at = now;

  await updateDoc(ref, payload);
}

/**
 * Elimina una plantilla por document id.
 */
export async function deleteMedicalTemplate(docId: string): Promise<void> {
  await deleteDoc(doc(db, MEDICAL_TEMPLATES_COLLECTION, docId));
}
